/**
 * CsiTailoringPanel — runs an engagement-specific re-rendering of the
 * recommended CSI moves: sector lens, regulatory anchors, key artefacts
 * to request, and a sponsor one-liner the engagement lead can paste into
 * a SteerCo paper.
 *
 * Renders inside the admin Assessment "view" page alongside RunPlanPanel
 * and EvidenceReviewPanel. Cached server-side — no Opus tokens spent on
 * a refresh unless the selected move set changes.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Wand2, RefreshCw, FileText, ChevronDown, ChevronUp, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import GenerationProgress from "./GenerationProgress";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

function MoveCard({ move, index }) {
  const [open, setOpen] = useState(index < 2);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(move.sponsor_one_liner || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="border border-zinc-200 bg-white" data-testid={`tailored-move-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-4 py-3 flex items-start justify-between gap-3 hover:bg-[#FAF7FF]"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-[#7737F6] tracking-wide mb-1">MOVE {index + 1}</div>
          <div className="font-medium text-zinc-900 text-[14px]">{move.engagement_name}</div>
          <p className="text-[12.5px] text-zinc-600 mt-1 line-clamp-2">{move.engagement_description}</p>
        </div>
        {open ? <ChevronUp size={16} className="text-zinc-400 shrink-0 mt-1"/> : <ChevronDown size={16} className="text-zinc-400 shrink-0 mt-1"/>}
      </button>
      {open && (
        <div className="border-t border-zinc-200 px-4 py-4 bg-zinc-50 space-y-3 text-[13px]">
          {move.sector_lens && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-1">Sector lens</div>
              <p className="text-zinc-700 italic">{move.sector_lens}</p>
            </div>
          )}
          {move.regulatory_anchors?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-1">Regulatory anchors</div>
              <div className="flex flex-wrap gap-1.5">
                {move.regulatory_anchors.map((a, i) => (
                  <span key={i} className="inline-block bg-white border border-zinc-300 text-zinc-700 text-[11.5px] px-2 py-0.5">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
          {move.key_artefacts_requested?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-1">Key artefacts to request</div>
              <ul className="space-y-0.5">
                {move.key_artefacts_requested.map((a, i) => (
                  <li key={i} className="text-zinc-700">· {a}</li>
                ))}
              </ul>
            </div>
          )}
          {move.watch_outs?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-1">Watch-outs</div>
              <ul className="space-y-0.5">
                {move.watch_outs.map((w, i) => (
                  <li key={i} className="text-amber-800">· {w}</li>
                ))}
              </ul>
            </div>
          )}
          {move.sponsor_one_liner && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-1 flex items-center justify-between">
                <span>Sponsor one-liner</span>
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex items-center gap-1 text-[10.5px] text-[#7737F6] hover:text-[#5D28C7]"
                  data-testid={`tailored-copy-${index}`}
                >
                  {copied ? <CheckCircle2 size={11}/> : <Copy size={11}/>} {copied ? "copied" : "copy"}
                </button>
              </div>
              <p className="bg-white border border-zinc-200 text-zinc-800 px-3 py-2">{move.sponsor_one_liner}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CsiTailoringPanel({ runId }) {
  const [tailoring, setTailoring] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);
  const toastRef = useRef(null);
  const stopPolling = useCallback(() => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/csi-tailoring`);
      setTailoring(data.tailoring);
      if (data.tailoring?.status === "generating") setGenerating(true);
    } catch (e) {
      if (e?.response?.status !== 404) {
        toast.error(e?.response?.data?.detail || "CSI tailoring load failed");
      }
      setTailoring(null);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); return () => stopPolling(); }, [load, stopPolling]);

  useEffect(() => {
    if (!generating || pollRef.current) return;
    const startedAt = Date.now();
    const MAX_POLL_MS = 9 * 60 * 1000;
    const tick = async () => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        stopPolling(); setGenerating(false);
        toast.error("This is taking longer than expected. Refresh or regenerate to check.", { id: toastRef.current });
        toastRef.current = null;
        return;
      }
      try {
        const { data } = await api.get(`/admin/assessments/runs/${runId}/csi-tailoring`);
        setTailoring(data.tailoring);
        const st = data.tailoring?.status || "ready";
        if (st === "ready") { stopPolling(); setGenerating(false); toast.success("CSI tailoring ready", { id: toastRef.current }); toastRef.current = null; }
        else if (st === "failed") { stopPolling(); setGenerating(false); toast.error(data.tailoring?.error || "Tailoring failed", { id: toastRef.current }); toastRef.current = null; }
      } catch { /* keep polling */ }
    };
    pollRef.current = setInterval(tick, 4000);
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating, runId]);

  const generate = useCallback(async () => {
    if (generating) return;
    setGenerating(true);
    const ts = toast.loading("Queued — tailoring moves in the background (~1 min)");
    toastRef.current = ts;
    try {
      const { data } = await api.post(
        `/admin/assessments/runs/${runId}/csi-tailoring`,
        { force_regenerate: !!tailoring && tailoring.status === "ready" },
      );
      setTailoring(data.tailoring);
      if (data.cached) { setGenerating(false); toast.success("Loaded from cache", { id: ts }); }
      else if (data.status === "ready") { setGenerating(false); toast.success("Tailoring ready", { id: ts }); }
      else { toast.loading("Generating — polling for completion…", { id: ts }); }
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || e.message || "CSI tailoring failed", { id: ts });
    }
  }, [runId, tailoring, generating]);

  if (loading && !tailoring) return <div className="bg-white border border-zinc-200 p-5 text-zinc-500 text-[13px]">Loading CSI tailoring…</div>;

  const pl = tailoring?.payload;
  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="csi-tailoring-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
            <Wand2 size={14}/> CSI move tailoring
          </div>
          {tailoring && (
            <div className="text-[11px] text-zinc-400 mt-1">
              {(tailoring.csi_move_ids || []).length} moves · {fmtDate(tailoring.generated_at)}
            </div>
          )}
        </div>
      </div>

      <GenerationProgress active={generating} label="Tailoring moves" typical="~1 min" testid="csi-generation-progress" />

      <button
        type="button"
        onClick={generate}
        disabled={generating}
        className="w-full bg-[#7737F6] hover:bg-[#5D28C7] disabled:opacity-60 text-white text-[13px] font-medium px-3 py-2 flex items-center justify-center gap-2 transition-colors"
        data-testid="csi-tailor-generate"
      >
        {generating ? <RefreshCw size={14} className="animate-spin"/> : <Wand2 size={14}/>}
        {generating ? "Tailoring…" : tailoring ? "Re-tailor moves" : "Tailor moves"}
      </button>

      <p className="text-[10.5px] text-zinc-500 mt-2 leading-relaxed">
        Catalogue stays untouched — this is an engagement-specific lens for the SteerCo paper.
      </p>

      {pl && (
        <div className="mt-5 space-y-4">
          {pl.portfolio_one_liner && (
            <div className="border-l-2 border-[#7737F6] pl-3 text-[13px] text-zinc-800">
              {pl.portfolio_one_liner}
            </div>
          )}
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2 flex items-center gap-1">
              <FileText size={11}/> Tailored moves
            </div>
            <div className="space-y-2">
              {(pl.tailored_moves || []).map((m, i) => (
                <MoveCard key={m.csi_move_id || i} move={m} index={i} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
